<template>
  <v-dialog v-model="dialog" max-width="680" persistent :fullscreen="$vuetify.breakpoint.smAndDown">
    <v-card class="exc-card" :class="{ 'exc-card--fullscreen': $vuetify.breakpoint.smAndDown }">

      <!-- ── Header ──────────────────────────────────────────────────────── -->
      <div class="exc-header">
        <div class="exc-header-icon">
          <v-icon size="18" color="white">mdi-account-cancel-outline</v-icon>
        </div>
        <div>
          <div class="exc-header-title">Exceções de Clientes</div>
          <div class="exc-header-sub">Clientes que não recebem e-book em seus pedidos</div>
        </div>
        <v-spacer />
        <v-btn icon small color="white" @click="fechar">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pt-4 pb-3 px-3 px-sm-5 exc-card-text">

        <!-- ── Formulário de adição ─────────────────────────────────────── -->
        <div class="exc-form-card">
          <div class="exc-form-row">
            <SeletorCliente
                v-model="novoCliente"
                label="Cliente *"
                placeholder="Nome ou código"
                :disabled="salvando || carregando"
                :clearable="true"
                :show-check-icon="false"
                style="flex: 1;"
            />
            <v-text-field
                v-model="novoMotivo"
                label="Motivo *"
                outlined dense
                :hide-details="!novoMotivo || novoMotivo.trim().length >= 8"
                :error="!!novoMotivo && novoMotivo.trim().length > 0 && novoMotivo.trim().length < 8"
                :error-messages="!!novoMotivo && novoMotivo.trim().length > 0 && novoMotivo.trim().length < 8 ? `Mínimo 8 caracteres (${novoMotivo.trim().length}/8)` : ''"
                :disabled="salvando || carregando"
                prepend-inner-icon="mdi-comment-text-outline"
                placeholder="Motivo da exceção..."
                maxlength="255"
                style="border-radius: 8px; flex: 1;"
            />
            <v-btn
                color="primary" elevation="0"
                :loading="salvando"
                :disabled="!podAdicionar || carregando"
                class="exc-form-btn"
                @click="adicionar"
            >
              <v-icon small :left="$vuetify.breakpoint.smAndDown">mdi-plus</v-icon>
              <span v-if="$vuetify.breakpoint.smAndDown">Adicionar</span>
            </v-btn>
          </div>

          <v-slide-y-transition>
            <v-alert
                v-if="erroAdicionar"
                type="warning" dense outlined
                class="mt-2 mb-0"
                style="font-size: 12px; border-radius: 8px;"
            >
              <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 6px;">
                <span>{{ erroAdicionar }}</span>
                <v-btn
                    v-if="clienteDuplicado"
                    x-small text color="warning"
                    style="font-size: 11px; text-transform: none; font-weight: 700; padding: 0 6px;"
                    @click="filtrarClienteDuplicado"
                >
                  <v-icon x-small left>mdi-magnify</v-icon>
                  Ver na lista
                </v-btn>
              </div>
            </v-alert>
          </v-slide-y-transition>
        </div>

        <v-divider class="my-3" />

        <!-- ── Cabeçalho da lista ───────────────────────────────────────── -->
        <!-- ── Busca + Tabs ─────────────────────────────────────────────── -->
        <div class="exc-list-toolbar">
          <button
              class="exc-tab"
              :class="{ 'exc-tab--ativo': aba === 'minha' }"
              @click="aba = 'minha'"
          >
            <v-icon size="13" class="mr-1">mdi-store-outline</v-icon>
            Minha Lista
            <span class="exc-tab-count">{{ minhasExcecoes.length }}</span>
          </button>

          <button
              class="exc-tab"
              :class="{ 'exc-tab--ativo': aba === 'todas' }"
              @click="aba = 'todas'"
          >
            <v-icon size="13" class="mr-1">mdi-domain</v-icon>
            Todas as Listas
            <span class="exc-tab-count">{{ excecoes.length }}</span>
          </button>

          <v-text-field
              v-if="excecoes.length || carregando"
              v-model="buscaLista"
              placeholder="Buscar na lista..."
              outlined dense hide-details clearable
              prepend-inner-icon="mdi-magnify"
              class="exc-busca-lista"
          />
        </div>

        <!-- Skeleton -->
        <div v-if="carregando" class="exc-skeleton-wrap">
          <div v-for="n in 4" :key="n" class="exc-skeleton-item" :style="{ opacity: 1 - n * 0.2 }">
            <div class="sk sk-text" style="width: 60px;" />
            <div style="flex: 1;">
              <div class="sk sk-text" style="width: 70%; margin-bottom: 6px;" />
              <div class="sk sk-text" style="width: 50%;" />
            </div>
            <div class="sk sk-chip" style="width: 52px;" />
          </div>
        </div>

        <!-- Vazio geral -->
        <div v-else-if="!excecoes.length" class="exc-empty">
          <v-icon size="40" color="grey lighten-2" class="mb-2">mdi-account-check-outline</v-icon>
          <span class="grey--text text--lighten-1 caption">Nenhum cliente na lista de exceção</span>
        </div>

        <!-- Com dados: tabs ou resultado de busca -->
        <template v-else>

          <!-- ── Resultado unificado de busca ────────────────────────────── -->
          <template v-if="buscaAtiva">
            <div class="exc-busca-info">
              <v-icon size="13" color="#64748b" class="mr-1">mdi-filter-outline</v-icon>
              {{ excecoesBuscadas.length }} resultado{{ excecoesBuscadas.length !== 1 ? 's' : '' }} para
              <strong>"{{ buscaLista }}"</strong>
            </div>
            <div v-if="!excecoesBuscadas.length" class="exc-empty">
              <v-icon size="40" color="grey lighten-2" class="mb-2">mdi-account-search-outline</v-icon>
              <span class="grey--text text--lighten-1 caption">Nenhum cliente encontrado</span>
            </div>
            <div v-else class="exc-list">
              <div
                  v-for="(exc, i) in excecoesBuscadas"
                  :key="exc.idclifor"
                  class="exc-item"
                  :class="{ 'exc-item--alt': i % 2 !== 0 }"
              >
                <div class="exc-item-left">
                  <div class="exc-item-badge">#{{ exc.idclifor }}</div>
                  <div class="exc-item-info">
                    <div class="exc-item-nome">{{ exc.nomecliente }}</div>
                    <div v-if="exc.nomeusuario" class="exc-item-loja">
                      <v-icon size="9" color="#0477a1" class="mr-1">mdi-domain</v-icon>
                      {{ exc.nomeusuario }}
                    </div>
                    <div class="exc-item-meta">
                      <template v-if="exc.motivo">
                        <v-icon size="9" color="#b0c4d4" class="mr-1">mdi-comment-text-outline</v-icon>
                        <span class="exc-item-motivo">{{ exc.motivo }}</span>
                      </template>
                      <template v-if="exc.dtinsercao">
                        <span v-if="exc.motivo" class="exc-item-sep">·</span>
                        <v-icon size="9" color="#b0c4d4" class="mr-1">mdi-clock-outline</v-icon>
                        {{ exc.dtinsercao }}
                      </template>
                    </div>
                  </div>
                </div>
                <v-tooltip bottom :color="ehMinhaExcecao(exc) ? 'error' : 'grey darken-1'">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn icon x-small
                             :color="ehMinhaExcecao(exc) ? 'error' : 'grey lighten-1'"
                             :loading="removendoId === exc.idclifor"
                             :disabled="salvando || carregando || !ehMinhaExcecao(exc)"
                             @click="remover(exc)"
                      >
                        <v-icon small>mdi-account-minus-outline</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span>{{ ehMinhaExcecao(exc) ? 'Remover exceção' : 'Somente quem adicionou pode remover' }}</span>
                </v-tooltip>
              </div>
            </div>
          </template>

          <!-- ── Tabs normais ────────────────────────────────────────────── -->
          <template v-else>
            <!-- Lista da aba ativa -->
            <div v-if="!listaAtiva.length" class="exc-empty" style="padding: 20px 16px;">
              <v-icon size="36" color="grey lighten-2" class="mb-2">mdi-account-check-outline</v-icon>
              <span class="grey--text text--lighten-1 caption">
                {{ aba === 'minha' ? 'Sua lista não possui exceções cadastradas' : 'Nenhuma exceção cadastrada' }}
              </span>
            </div>
            <div v-else class="exc-list">
              <div
                  v-for="(exc, i) in listaAtiva"
                  :key="exc.idclifor"
                  class="exc-item"
                  :class="{ 'exc-item--alt': i % 2 !== 0 }"
              >
                <div class="exc-item-left">
                  <div class="exc-item-badge">#{{ exc.idclifor }}</div>
                  <div class="exc-item-info">
                    <div class="exc-item-nome">{{ exc.nomecliente }}</div>
                    <div v-if="aba === 'todas' && exc.nomeusuario" class="exc-item-loja">
                      <v-icon size="9" color="#0477a1" class="mr-1">mdi-domain</v-icon>
                      {{ exc.nomeusuario }}
                    </div>
                    <div class="exc-item-meta">
                      <template v-if="exc.motivo">
                        <v-icon size="9" color="#b0c4d4" class="mr-1">mdi-comment-text-outline</v-icon>
                        <span class="exc-item-motivo">{{ exc.motivo }}</span>
                      </template>
                      <template v-if="exc.dtinsercao">
                        <span v-if="exc.motivo" class="exc-item-sep">·</span>
                        <v-icon size="9" color="#b0c4d4" class="mr-1">mdi-clock-outline</v-icon>
                        {{ exc.dtinsercao }}
                      </template>
                    </div>
                  </div>
                </div>
                <v-tooltip bottom :color="ehMinhaExcecao(exc) ? 'error' : 'grey darken-1'">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn
                          icon x-small
                          :color="ehMinhaExcecao(exc) ? 'error' : 'grey lighten-1'"
                          :loading="removendoId === exc.idclifor"
                          :disabled="salvando || carregando || !ehMinhaExcecao(exc)"
                          @click="remover(exc)"
                      >
                        <v-icon small>mdi-account-minus-outline</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span>{{ ehMinhaExcecao(exc) ? 'Remover exceção' : 'Somente quem adicionou pode remover' }}</span>
                </v-tooltip>
              </div>
            </div>
          </template>

        </template>

      </v-card-text>

      <v-card-actions class="px-3 px-sm-5 pb-4 pt-2 dialog-actions">
        <v-btn text color="primary" @click="fechar">Fechar</v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import SeletorCliente from '@/Components/UI/Inputs/SeletorCliente.vue'

export default {
  name: 'DialogExcecaoClientes',

  components: { SeletorCliente },

  props: {
    value: { type: Boolean, default: false },
  },

  data: () => ({
    carregando:  false,
    salvando:    false,
    removendoId: null,
    excecoes:    [],
    novoCliente:  null,
    novoMotivo:   '',
    buscaLista:   '',
    aba:          'minha',
    erroAdicionar:    '',
    clienteDuplicado: null,
  }),

  computed: {
    ...mapState(['backendUrl', 'usuario']),

    dialog: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },

    podAdicionar() {
      return !!this.novoCliente && !!this.novoMotivo && this.novoMotivo.trim().length >= 8
    },

    buscaAtiva() {
      return !!(this.buscaLista && this.buscaLista.trim().length > 0)
    },

    minhasExcecoes() {
      const meuId = this.usuario?.iderpusuario
      if (!meuId) return this.excecoes
      const backendEnviaId = this.excecoes.some(e => e.idusuario != null)
      if (!backendEnviaId) return this.excecoes
      return this.excecoes.filter(e => Number(e.idusuario) === Number(meuId))
    },

    listaAtiva() {
      return this.aba === 'minha' ? this.minhasExcecoes : this.excecoes
    },

    // Busca unificada em todas as exceções
    excecoesBuscadas() {
      if (!this.buscaAtiva) return []
      const termo = this.buscaLista.trim().toLowerCase()
      return this.excecoes.filter(e =>
          (e.nomecliente && e.nomecliente.toLowerCase().includes(termo)) ||
          (e.nomeusuario && e.nomeusuario.toLowerCase().includes(termo)) ||
          (e.motivo      && e.motivo.toLowerCase().includes(termo))      ||
          String(e.idclifor).includes(termo)
      )
    },
  },

  watch: {
    value(aberto) {
      if (aberto) {
        this.novoCliente      = null
        this.novoMotivo       = ''
        this.buscaLista       = ''
        this.aba              = 'minha'
        this.erroAdicionar    = ''
        this.clienteDuplicado = null
        this.listar()
      }
    },
  },

  methods: {
    ehMinhaExcecao(exc) {
      const meuId = this.usuario?.iderpusuario
      if (!meuId || exc.idusuario == null) return true
      return Number(exc.idusuario) === Number(meuId)
    },

    listar() {
      this.carregando = true
      axios
          .post(`${this.backendUrl}wolihub/excecao/listar`)
          .then(res => { this.excecoes = res.data || [] })
          .catch(() => { this.$root.$snackbar.showError('Erro ao carregar exceções.') })
          .finally(() => { this.carregando = false })
    },

    adicionar() {
      if (!this.novoCliente) return
      this.erroAdicionar = ''

      const id = Number(this.novoCliente.idcliente)
      const jaExiste = this.excecoes.find(e => Number(e.idclifor) === id)

      if (jaExiste) {
        this.erroAdicionar    = `${this.novoCliente.nome} já está na lista de exceção.`
        this.clienteDuplicado = jaExiste
        setTimeout(() => { this.erroAdicionar = ''; this.clienteDuplicado = null }, 8000)
        return
      }

      this.salvando = true
      axios
          .post(`${this.backendUrl}wolihub/ebook/remover`, {
            idclifor: id,
            motivo:   this.novoMotivo.trim(),
          })
          .then(() => {
            this.novoCliente  = null
            this.novoMotivo   = ''
            this.erroAdicionar = ''
            this.listar()
            this.$root.$snackbar?.showSuccess('Cliente adicionado à lista de exceção.')
          })
          .catch(e => {
            const msg = e?.response?.data?.erro || 'Erro ao adicionar exceção.'
            this.erroAdicionar = msg
            setTimeout(() => { this.erroAdicionar = '' }, 5000)
          })
          .finally(() => { this.salvando = false })
    },

    remover(exc) {
      this.removendoId = exc.idclifor
      axios
          .post(`${this.backendUrl}wolihub/excecao/remover`, { idclifor: exc.idclifor })
          .then(() => {
            this.excecoes = this.excecoes.filter(e => e.idclifor !== exc.idclifor)
            this.$root.$snackbar.showSuccess('Exceção removida com sucesso.')
          })
          .catch(() => { this.$root.$snackbar.showError('Erro ao remover exceção.') })
          .finally(() => { this.removendoId = null })
    },

    filtrarClienteDuplicado() {
      if (!this.clienteDuplicado) return
      // aplica o código do cliente no filtro da lista
      this.buscaLista = String(this.clienteDuplicado.idclifor)
      // garante que a aba "todas" esteja visível se o cliente for de outra loja
      const id = Number(this.clienteDuplicado.idclifor)
      const estaEmMinhas = this.minhasExcecoes.some(e => Number(e.idclifor) === id)
      if (!estaEmMinhas) this.aba = 'todas'
      // limpa o erro
      this.erroAdicionar    = ''
      this.clienteDuplicado = null
    },

    fechar() {
      this.$emit('input', false)
    },
  },
}
</script>

<style scoped>
/* ── Card base ───────────────────────────────────────────────────────────── */
.exc-card {
  border-radius: 12px;
  overflow: hidden;
}
.exc-card--fullscreen {
  border-radius: 0 !important;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.exc-card--fullscreen >>> .exc-card-text {
  flex: 1;
  overflow-y: auto;
}

/* ── Header ─────────────────────────────────────────────────────────────── */
.exc-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #023449 0%, #035878 100%);
}
.exc-header-icon {
  width: 36px; height: 36px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.exc-header-title { font-size: 14px; font-weight: 700; color: white; line-height: 1.2; }
.exc-header-sub   { font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 1px; }

/* ── Formulário ──────────────────────────────────────────────────────────── */
.exc-form-card {
  background: #f8fafc;
  border: 1px solid #e2ecf4;
  border-radius: 10px;
  padding: 10px 12px;
}

.exc-form-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.exc-form-btn {
  border-radius: 8px !important;
  height: 40px !important;
  flex-shrink: 0;
}

/* ── Toolbar busca ───────────────────────────────────────────────────────── */
.exc-list-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

/* ── Tabs ────────────────────────────────────────────────────────────────── */
.exc-tab {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  outline: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  transition: all 0.18s ease;
  user-select: none;
  white-space: nowrap;
}
.exc-tab:hover { border-color: #023449; color: #023449; background: #f5f9fc; }
.exc-tab--ativo { background: #023449; border-color: #023449; color: #fff; }
.exc-tab--ativo .v-icon { color: rgba(255,255,255,0.85) !important; }

.exc-tab-count {
  font-size: 10px;
  font-weight: 700;
  background: rgba(255,255,255,0.22);
  border-radius: 4px;
  padding: 1px 5px;
  line-height: 1.4;
}
.exc-tab:not(.exc-tab--ativo) .exc-tab-count {
  background: #e2e8f0;
  color: #64748b;
}

/* ── Info de busca ───────────────────────────────────────────────────────── */
.exc-busca-info {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 8px;
  gap: 3px;
}

/* Campo busca na lista */
.exc-busca-lista {
  flex: 1;
  min-width: 0;
}
.exc-busca-lista >>> .v-input__slot   { min-height: 32px !important; border-radius: 8px; background: #f8fafc; }
.exc-busca-lista >>> .v-input__control{ min-height: 32px !important; }
.exc-busca-lista >>> fieldset         { border-color: #dde8f0; }
.exc-busca-lista >>> .v-input__slot:hover fieldset { border-color: #023449; }
.exc-busca-lista >>> .v-input--is-focused fieldset { border-color: #023449 !important; }

/* ── Skeleton ────────────────────────────────────────────────────────────── */
@keyframes shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
}
.sk {
  display: block; border-radius: 4px;
  background: linear-gradient(90deg, #dde6ee 25%, #edf3f8 50%, #dde6ee 75%);
  background-size: 400px 100%;
  animation: shimmer 1.4s infinite linear;
}
.sk-text { height: 11px; }
.sk-chip { height: 20px; border-radius: 10px; }

.exc-skeleton-wrap { display: flex; flex-direction: column; gap: 2px; }
.exc-skeleton-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 12px;
  border-bottom: 1px solid #f0f4f7;
}

/* ── Vazio ───────────────────────────────────────────────────────────────── */
.exc-empty {
  display: flex; flex-direction: column; align-items: center;
  padding: 28px 16px;
}

/* ── Lista ───────────────────────────────────────────────────────────────── */
.exc-list {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2ecf4;
  max-height: 340px;
  overflow-y: auto;
}

.exc-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #f0f4f7;
  transition: background 0.15s;
  gap: 10px;
}
.exc-item:last-child { border-bottom: none; }
.exc-item--alt       { background: #fafcfe; }
.exc-item:hover      { background: #f0f5f8; }

.exc-item-left {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.exc-item-badge {
  font-size: 11px; font-weight: 700;
  color: #fff; background: #023449;
  border-radius: 5px; padding: 2px 7px;
  flex-shrink: 0; margin-top: 1px;
}

.exc-item-info { min-width: 0; flex: 1; }

.exc-item-nome {
  font-size: 13px; font-weight: 600; color: #023449;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-bottom: 2px;
}

.exc-item-loja {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  color: #0477a1;
  background: #e0f4fb;
  border-radius: 4px;
  padding: 1px 6px;
  margin-bottom: 3px;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.exc-item-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 10px;
  color: #9aacbc;
  gap: 3px;
}

.exc-item-motivo {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
  font-style: italic;
}

.exc-item-sep { color: #c8d8e4; }

/* ── Scrollbar ───────────────────────────────────────────────────────────── */
.exc-list::-webkit-scrollbar       { width: 4px; }
.exc-list::-webkit-scrollbar-track { background: #f1f5f9; }
.exc-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }

/* ── Footer ──────────────────────────────────────────────────────────────── */
.dialog-actions { display: flex; justify-content: flex-end; }

/* ── Mobile (fullscreen) ─────────────────────────────────────────────────── */
@media (max-width: 600px) {
  /* Formulário empilhado */
  .exc-form-row { flex-direction: column; align-items: stretch; }
  .exc-form-btn { height: 44px !important; width: 100%; }

  /* Tabs ocupam linha própria, dividem o espaço */
  .exc-tab { flex: 1; justify-content: center; }

  /* Campo de busca quebra para linha própria abaixo das tabs */
  .exc-busca-lista { flex-basis: 100%; }

  /* Lista sem max-height: o card-text (flex: 1) já controla o scroll */
  .exc-list { max-height: none; overflow-y: visible; }

  /* Motivo sem largura fixa em telas estreitas */
  .exc-item-motivo { max-width: 100%; }

  /* Itens da lista um pouco maiores para toque */
  .exc-item { padding: 12px 10px; }

  /* Header subtitle oculto em telas muito pequenas */
  @media (max-width: 380px) {
    .exc-header-sub { display: none; }
  }
}
</style>
